/**
 * Care Concierge case log on the FA dashboard (Feature 106).
 *
 * The advisor's record of every consultation they initiated. The outcome shown here is whatever
 * the Care Concierge team chose to share plus the providers referred — their notes appear only
 * when they ticked the share flag, and the API does not return them otherwise.
 */

import { useState } from 'react';
import { FileText, RefreshCw, Undo2 } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useToast } from '@/shared/components/Toast';
import { downloadBlob } from '@/shared/utils/downloadBlob';
import {
  useCareConciergeCases,
  useRequestSessionReturn,
  useResendCareConciergeInvitation,
} from '../../hooks/useCareConcierge';
import { fetchCareConciergeCarePlan } from '../../api/careConciergeApi';
import type { CareConciergeCase, CaseStatus } from '../../api/careConciergeApi';

const STATUS_LABEL: Record<CaseStatus, string> = {
  AWAITING_CLIENT: 'Awaiting client',
  SCHEDULED: 'Scheduled',
  OCCURRED: 'Occurred',
  COMPLETED: 'Completed',
  NOT_COMPLETED: 'Not completed',
};

/** Where the consultation is still ahead of us, so the advisor could still join it. */
const UPCOMING: CaseStatus[] = ['AWAITING_CLIENT', 'SCHEDULED'];

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
};

const describeOutcome = (careCase: CareConciergeCase): string => {
  const { outcome, status } = careCase;

  if (status === 'NOT_COMPLETED') return 'Did not take place';
  if (!outcome) return status === 'SCHEDULED' ? 'Booked, awaiting the consultation' : 'Awaiting the consultation';

  const referred = outcome.referredProviders.length
    ? ` Referred: ${outcome.referredProviders.join(', ')}.`
    : '';

  return `${outcome.notes ?? `Consultation held ${formatDate(outcome.occurredOn)}.`}${referred}`;
};

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
    ?.message ??
  (error as { message?: string })?.message ??
  fallback;

export const CareConciergeCases = () => {
  const { showToast } = useToast();
  const { data: cases, isLoading, isError, refetch } = useCareConciergeCases();
  const resend = useResendCareConciergeInvitation();
  const requestReturn = useRequestSessionReturn();
  // Local rather than a mutation: nothing on the server changes, and the only state worth holding
  // is which row is mid-download.
  const [downloading, setDownloading] = useState<string | null>(null);

  if (isLoading) {
    return <Skeleton data-testid="care-concierge-cases-skeleton" className="mt-6 h-32 w-full" />;
  }

  // A failed load is not an empty log, and collapsing the two hid the advisor's record of every
  // consultation they had arranged, with no sign that anything went wrong.
  if (isError) {
    return (
      <div className="mt-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-800">Care Concierge Cases</h2>
        <Card className="flex flex-col items-start gap-3 p-4">
          <p className="text-sm text-destructive">We could not load your consultations.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </Card>
      </div>
    );
  }

  // Nothing initiated yet: the card above already explains the offer, so an empty table here
  // would only add furniture.
  if (!cases || cases.length === 0) return null;

  const handleRequestReturn = (caseId: string) => {
    requestReturn.mutate(
      { caseId },
      {
        onSuccess: () =>
          showToast('The Care Concierge team will review this and get back to you.', 'success'),
        onError: (error) => showToast(errorMessage(error, 'Could not send that request.'), 'error'),
      },
    );
  };

  const handleDownloadCarePlan = async (caseId: string) => {
    setDownloading(caseId);
    try {
      const { blob, fileName } = await fetchCareConciergeCarePlan(caseId);
      downloadBlob(blob, fileName);
    } catch (error) {
      showToast(errorMessage(error, 'Could not download the care plan.'), 'error');
    } finally {
      setDownloading(null);
    }
  };

  const handleResend = (caseId: string) => {
    resend.mutate(caseId, {
      onSuccess: () => showToast('Invitation resent.', 'success'),
      onError: (error) =>
        showToast(errorMessage(error, 'Could not resend the invitation.'), 'error'),
    });
  };

  return (
    <div className="mt-6">
      <h2 className="mb-1 text-lg font-semibold text-gray-800">Care Concierge Cases</h2>
      <p className="mb-4 text-sm text-gray-600">
        Consultations you have arranged, and what came of them.
      </p>

      <Card className="divide-y divide-border">
        {cases.map((careCase) => {
          const isUpcoming = UPCOMING.includes(careCase.status);
          const canRequestReturn = careCase.payer === 'SPONSOR' && !careCase.pendingReturnRequest;

          return (
            <div
              key={careCase.id}
              className="flex flex-wrap items-start justify-between gap-3 p-4"
            >
              <div className="min-w-0">
                <p className="font-medium">
                  {careCase.inviteeName || careCase.inviteeEmail}
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {careCase.payer === 'SPONSOR' ? 'Sponsored' : 'Self-pay'}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {/* "Invited" would be a lie on a case the advisor is booking themselves: the
                      client has heard nothing. */}
                  {careCase.invitationSent ? 'Invited' : 'Opened'}{' '}
                  {formatDate(careCase.invitedAt)} · {STATUS_LABEL[careCase.status]}
                  {careCase.scheduledAt && ` · ${formatDateTime(careCase.scheduledAt)}`}
                  {careCase.sendCount > 1 && ` · resent ${formatDate(careCase.lastSentAt)}`}
                  {!careCase.invitationSent && ' · you are booking this one'}
                </p>
                <p className="mt-1 text-sm text-gray-600">{describeOutcome(careCase)}</p>

                {/* Requirement v4 item 12: shown only when the team shared the case, and absent
                    rather than disabled when they did not — a greyed-out button would tell the
                    advisor a plan exists that they were not meant to know about.

                    Filled and full-height rather than a bare link: on a case that is over this is
                    the only thing left to do with it, and as underlined text it read as part of
                    the note above rather than as something to press. */}
                {careCase.outcome?.carePlanAvailable && (
                  <Button
                    type="button"
                    size="sm"
                    className="mt-2"
                    disabled={downloading === careCase.id}
                    onClick={() => handleDownloadCarePlan(careCase.id)}
                  >
                    <FileText className="mr-1.5 h-4 w-4" aria-hidden="true" />
                    {downloading === careCase.id ? 'Downloading…' : 'Download care plan'}
                  </Button>
                )}

                {/* Only while the consultation is still ahead of us: joining one that already
                    happened is not a thing, and consent for it would be a strange thing to ask. */}
                {isUpcoming && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Want to join? Ask your client first — we&rsquo;ll need their consent, and they
                    can add you as a guest when they book.
                  </p>
                )}

                {/* The case still reads Awaiting client while a request is pending — nothing has
                    been decided — so without this the advisor would click and see no change. */}
                {careCase.pendingReturnRequest && (
                  <p className="mt-1 text-sm text-amber-700">
                    Session return requested{' '}
                    {formatDate(careCase.pendingReturnRequest.requestedAt)} · awaiting the Care
                    Concierge team.
                  </p>
                )}

                {/* A denial has to be visible, or the advisor assumes the session came back. */}
                {careCase.deniedReturnRequest && (
                  <p className="mt-1 text-sm text-destructive">
                    Session return declined
                    {careCase.deniedReturnRequest.decisionNote
                      ? `: ${careCase.deniedReturnRequest.decisionNote}`
                      : '.'}
                  </p>
                )}
              </div>

              {isUpcoming && (
                <div className="flex flex-col items-start gap-1 sm:items-end">
                  <div className="flex flex-wrap gap-2">
                    {careCase.status === 'AWAITING_CLIENT' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={resend.isPending}
                        onClick={() => handleResend(careCase.id)}
                      >
                        <RefreshCw className="mr-1 h-3 w-3" aria-hidden="true" />
                        {/* Nothing has been sent on the on-behalf path, so "Resend" would be
                            describing an event that never happened. Same endpoint either way. */}
                        {careCase.invitationSent ? 'Resend' : 'Send the client the link'}
                      </Button>
                    )}

                    {/* Sponsored only: a self-pay consultation used no session, so there is nothing
                        to ask back. Hidden once a request is open — one at a time. */}
                    {canRequestReturn && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={requestReturn.isPending}
                        onClick={() => handleRequestReturn(careCase.id)}
                      >
                        <Undo2 className="mr-1 h-3 w-3" aria-hidden="true" />
                        Consultation didn&rsquo;t happen
                      </Button>
                    )}
                  </div>

                  {/* The button alone reads as an action with an unknown consequence — the
                      advisor cannot tell whether it cancels the case, emails the client, or costs
                      them something. Saying that it opens a review, and that the session may come
                      back, is what makes it safe to press. "Put back" rather than "credited to
                      your account": the session returns to the pool it was spent from, which for
                      most advisors is their firm's, not their own. */}
                  {canRequestReturn && (
                    <p className="max-w-[18rem] text-xs text-muted-foreground sm:text-right">
                      Let us know if the consultation didn&rsquo;t take place. The Care Concierge
                      team will review it and put the session back if appropriate.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Card>
    </div>
  );
};
